/**
 * Sport Color Helper
 *
 * Returns sport-specific colors based on sport_id.
 * Color convention:
 * - Dogs 6 & 8 (sport_id: 10, 11): Muted green #166534
 * - Horses 6 & 8 (sport_id: 20, 21): Football blue #1a3a5f
 * - Baseball (sport_id: 40): Muted orange #c2410c
 * - Football (sport_id: 50): Brand Yellow #ffd03a
 */

function parseSportId(sport_id: number | string): number {
  return typeof sport_id === 'string' ? parseInt(sport_id, 10) : sport_id;
}

/**
 * Get the primary color for a sport based on sport_id.
 */
export function getSportColor(sport_id: number | string): string {
  const sportIdNum = parseSportId(sport_id);

  if (sportIdNum === 10 || sportIdNum === 11) {
    return '#166534';
  }
  if (sportIdNum === 20 || sportIdNum === 21) {
    return '#1a3a5f';
  }
  if (sportIdNum === 40) {
    return '#c2410c';
  }
  if (sportIdNum === 50) {
    return '#ffd03a';
  }
  return '#166534';
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function hexToRgb(hex: string): Rgb | null {
  if (!hex || typeof hex !== 'string') return null;
  const raw = hex.trim().replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  if (full.length !== 6) return null;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if (![r, g, b].every((v) => Number.isFinite(v))) return null;
  return { r, g, b };
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const srgb = [r, g, b].map((v) => v / 255).map((c) => {
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * srgb[0]! + 0.7152 * srgb[1]! + 0.0722 * srgb[2]!;
}

/**
 * Get contrasting text color (dark or white) for a sport background.
 */
export function getSportOnColor(sport_id: number | string): string {
  const hex = getSportColor(sport_id);
  const rgb = hexToRgb(hex);
  if (!rgb) return '#ffffff';
  return relativeLuminance(rgb) > 0.62 ? '#111827' : '#ffffff';
}

/**
 * Get Tailwind CSS classes for a sport color (background).
 */
export function getSportColorClasses(sport_id: number | string): string {
  const color = getSportColor(sport_id);
  return `bg-[${color}]`;
}

/**
 * Get Tailwind CSS classes for text color on sport background.
 */
export function getSportTextColorClasses(sport_id: number | string): string {
  const on = getSportOnColor(sport_id);
  return on === '#111827' ? 'text-gray-900' : 'text-white';
}

/**
 * Get Tailwind CSS classes for border color.
 */
export function getSportBorderColorClasses(sport_id: number | string): string {
  const color = getSportColor(sport_id);
  return `border-[${color}]`;
}

/**
 * Get Tailwind CSS classes for hover state.
 */
export function getSportHoverColorClasses(sport_id: number | string): string {
  const color = getSportColor(sport_id);
  const on = getSportOnColor(sport_id);
  const text = on === '#111827' ? 'hover:text-gray-900' : 'hover:text-white';
  return `hover:bg-[${color}] ${text}`;
}

/**
 * Get inline style object for background color.
 */
export function getSportColorStyle(sport_id: number | string): { backgroundColor: string } {
  return {
    backgroundColor: getSportColor(sport_id),
  };
}

/**
 * Get a light version of the sport color for backgrounds (e.g. tab headers).
 */
export function getSportLightColor(sport_id: number | string): string {
  const sportIdNum = parseSportId(sport_id);

  if (sportIdNum === 10 || sportIdNum === 11) {
    return '#166534';
  }
  if (sportIdNum === 20 || sportIdNum === 21) {
    return '#1a3a5f';
  }
  if (sportIdNum === 40) {
    return '#c2410c';
  }
  if (sportIdNum === 50) {
    return '#ffd03a';
  }
  return '#166534';
}

/**
 * Normalize sport_id for image paths (e.g. 11 → 10, 21 → 20).
 */
export function getSportIdForImagePath(sport_id: number | string): number {
  const sportIdNum = parseSportId(sport_id);
  if (sportIdNum === 11) return 10;
  if (sportIdNum === 21) return 20;
  return sportIdNum;
}
